<script setup>
import { computed, reactive } from 'vue'
import { useRouter } from 'vue-router'
import MainLayout from '@/components/layout/MainLayout.vue'
import PageHeader from '@/components/common/PageHeader.vue'
import PaymentInvoiceSelector from '@/components/payments/PaymentInvoiceSelector.vue'
import PaymentMethodForm from '@/components/payments/PaymentMethodForm.vue'
import PaymentSlipUpload from '@/components/payments/PaymentSlipUpload.vue'
import PaymentFormSummary from '@/components/payments/PaymentFormSummary.vue'
import { usePaymentStore } from '@/stores/payment'

const router = useRouter()
const paymentStore = usePaymentStore()

const today = new Date().toISOString().slice(0, 10)

const form = reactive({
  invoiceNo: '',
  paymentDate: today,
  amount: '',
  method: '',
  bankName: '',
  referenceNo: '',
  status: 'completed',
  generateReceipt: true,
  note: '',
  slipFileName: '',
})

const errors = reactive({
  invoiceNo: '',
  paymentDate: '',
  amount: '',
  method: '',
})

const selectedInvoice = computed(() => paymentStore.getInvoiceByNo(form.invoiceNo))

const canSubmit = computed(() => {
  return !!form.invoiceNo && !!form.paymentDate && Number(form.amount) > 0 && !!form.method
})

const resetErrors = () => {
  errors.invoiceNo = ''
  errors.paymentDate = ''
  errors.amount = ''
  errors.method = ''
}

const handleInvoiceChange = (invoiceNo) => {
  form.invoiceNo = invoiceNo
  const invoice = paymentStore.getInvoiceByNo(invoiceNo)
  form.amount = invoice ? invoice.outstandingAmount : ''
  if (invoice?.status === 'partial') {
    form.status = 'partial'
  } else {
    form.status = 'completed'
  }
}

const attachMockSlip = () => {
  form.slipFileName = `mock-slip-${Date.now()}.png`
}

const validateForm = () => {
  resetErrors()

  if (!form.invoiceNo) {
    errors.invoiceNo = 'กรุณาเลือกใบแจ้งหนี้'
  }

  if (!form.paymentDate) {
    errors.paymentDate = 'กรุณาระบุวันที่รับชำระ'
  }

  if (!form.amount || Number(form.amount) <= 0) {
    errors.amount = 'กรุณาระบุจำนวนเงินที่มากกว่า 0'
  }

  if (!form.method) {
    errors.method = 'กรุณาเลือกช่องทางชำระ'
  }

  return !errors.invoiceNo && !errors.paymentDate && !errors.amount && !errors.method
}

const handleSubmit = async () => {
  if (!validateForm()) return

  const result = await paymentStore.createPayment({
    invoiceNo: form.invoiceNo,
    paymentDate: form.paymentDate,
    amount: form.amount,
    method: form.method,
    bankName: form.bankName,
    referenceNo: form.referenceNo,
    status: form.status,
    generateReceipt: form.generateReceipt,
    note: form.note,
  })

  if (result.success) {
    window.alert('บันทึกรายการรับชำระสำเร็จ')
    router.push(`/payments/${result.id}`)
  }
}

const goToPaymentList = () => {
  router.push('/payments')
}
</script>

<template>
  <MainLayout>
    <div class="container-fluid px-0">
      <PageHeader
        title="ฟอร์มรับชำระเงิน"
        subtitle="บันทึกรายการรับชำระค่าส่วนกลางจากลูกบ้าน พร้อมเลือกใบแจ้งหนี้และแนบหลักฐานการชำระ"
        button-text="กลับไปรายการรับชำระ"
        button-icon="bi-arrow-left"
        button-color="secondary"
        @action="goToPaymentList"
      />

      <div class="row g-4 align-items-start">
        <div class="col-xl-8 vstack gap-4">
          <PaymentInvoiceSelector
            :invoice-no="form.invoiceNo"
            :invoice="selectedInvoice"
            :invoice-options="paymentStore.paymentFormInvoiceOptions"
            :error="errors.invoiceNo"
            @update:invoiceNo="handleInvoiceChange"
          />

          <PaymentMethodForm
            :payment-date="form.paymentDate"
            :amount="form.amount"
            :model-value="form.method"
            :bank-name="form.bankName"
            :reference-no="form.referenceNo"
            :status="form.status"
            :generate-receipt="form.generateReceipt"
            :note="form.note"
            :methods="paymentStore.paymentMethods"
            :errors="errors"
            @update:paymentDate="form.paymentDate = $event"
            @update:amount="form.amount = $event"
            @update:modelValue="form.method = $event"
            @update:bankName="form.bankName = $event"
            @update:referenceNo="form.referenceNo = $event"
            @update:status="form.status = $event"
            @update:generateReceipt="form.generateReceipt = $event"
            @update:note="form.note = $event"
          />

          <PaymentSlipUpload
            :file-name="form.slipFileName"
            @select-mock-file="attachMockSlip"
          />
        </div>

        <div class="col-xl-4">
          <PaymentFormSummary
            :invoice="selectedInvoice"
            :amount="form.amount"
            :method="form.method"
            :status="form.status"
            :generate-receipt="form.generateReceipt"
            :disabled="!canSubmit"
            :submitting="paymentStore.submitting"
            @submit="handleSubmit"
          />
        </div>
      </div>
    </div>
  </MainLayout>
</template>
